import React, { useState, useEffect } from "react";
import { adminApi } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { LayoutDashboard, Users, Library, FileText, Settings, Shield, LogOut, ScrollText, ExternalLink, Coins, Stamp, TrendingUp } from "lucide-react";
import AdminOverview from "../components/admin/AdminOverview";
import AdminUsers from "../components/admin/AdminUsers";
import AdminRoles from "../components/admin/AdminRoles";
import AdminContent from "../components/admin/AdminContent";
import AdminSettings from "../components/admin/AdminSettings";
import AdminActivity from "../components/admin/AdminActivity";
import AdminTemplates from "../components/admin/AdminTemplates";
import AdminMemberships from "../components/admin/AdminMemberships";
import AdminJobSources from "../components/admin/AdminJobSources";
import AdminAccelerator from "../components/admin/AdminAccelerator";
import AdminBranding from "../components/admin/AdminBranding";
import AdminFutureJobs from "../components/admin/AdminFutureJobs";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "roles", label: "Role Library", icon: Library },
  { id: "content", label: "Content", icon: FileText },
  { id: "templates", label: "Templates", icon: FileText },
  { id: "memberships", label: "Memberships", icon: Users },
  { id: "jobs", label: "Job Sources", icon: ExternalLink },
  { id: "trends", label: "Trend Lists", icon: TrendingUp },
  { id: "accelerator", label: "2nd Income", icon: Coins },
  { id: "activity", label: "Activity Log", icon: ScrollText },
  { id: "branding", label: "Report Branding", icon: Stamp },
  { id: "settings", label: "Settings", icon: Settings },
];

const AdminDashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [admin, setAdmin] = useState(null);

  useEffect(() => { adminApi.get("/admin/me").then((r) => setAdmin(r.data)).catch(() => onLogout()); }, []);

  const onAuthError = (e) => { if (e?.response?.status === 401) onLogout(); };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="admin-dashboard">
      <aside className="md:w-60 shrink-0 bg-white border-b md:border-b-0 md:border-r border-slate-200 flex md:flex-col">
        <div className="p-5 flex items-center gap-2 border-r md:border-r-0 md:border-b border-slate-200">
          <div className="w-9 h-9 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center"><Shield className="w-4 h-4 text-sky-600" /></div>
          <div className="hidden md:block"><p className="font-bold text-slate-900 leading-none">Admin</p><p className="text-[10px] text-slate-500 mt-1 truncate max-w-[9rem]">{admin?.email}</p></div>
        </div>
        <nav className="flex md:flex-col md:p-3 flex-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2.5 px-4 py-3 md:rounded-lg text-sm whitespace-nowrap transition-colors ${tab === t.id ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-l md:border-l-0 md:border-t border-slate-200 flex md:flex-col gap-1">
          <button onClick={() => { sessionStorage.setItem("tj-skip-admin-redirect", "1"); window.location.href = "/app"; }} className="flex items-center gap-2 px-4 py-2 md:rounded-lg text-sm text-slate-500 hover:text-slate-800 hover:bg-slate-100 whitespace-nowrap" data-testid="admin-open-app"><ExternalLink className="w-4 h-4" /> <span className="hidden md:inline">Open user app</span></button>
          <Button variant="ghost" onClick={onLogout} className="w-full justify-start text-slate-500 hover:text-rose-600 hover:bg-rose-50" data-testid="admin-logout"><LogOut className="w-4 h-4 mr-2" /> <span className="hidden md:inline">Log out</span></Button>
        </div>
      </aside>
      <main className="flex-1 p-5 md:p-8 overflow-x-hidden">
        <h1 className="text-2xl font-bold text-slate-900 mb-6 capitalize">{TABS.find((t) => t.id === tab)?.label}</h1>
        {tab === "overview" && <AdminOverview onAuthError={onAuthError} />}
        {tab === "users" && <AdminUsers onAuthError={onAuthError} />}
        {tab === "roles" && <AdminRoles onAuthError={onAuthError} />}
        {tab === "content" && <AdminContent onAuthError={onAuthError} />}
        {tab === "activity" && <AdminActivity onAuthError={onAuthError} />}
        {tab === "templates" && <AdminTemplates />}
        {tab === "memberships" && <AdminMemberships />}
        {tab === "jobs" && <AdminJobSources />}
        {tab === "trends" && <AdminFutureJobs onAuthError={onAuthError} />}
        {tab === "accelerator" && <AdminAccelerator />}
        {tab === "branding" && <AdminBranding onAuthError={onAuthError} />}
        {tab === "settings" && <AdminSettings onAuthError={onAuthError} />}
      </main>
    </div>
  );
};

export default AdminDashboard;
